import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiShield, FiShieldOff, FiTrash2 } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import companyService from "../../../services/company.service";
import DataTableToolbar from "../../../components/common/DataTableToolbar";
import StatusFilterSelect from "../../../components/common/StatusFilterSelect";
import PaginationBar from "../../../components/common/PaginationBar";
import { printTablePage } from "../../../utils/printTable";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readPagination } from "../../../utils/listPagination";
import BlockCompanyModal from "../../../components/common/BlockCompanyModal";
import ResetPasswordModal from "../../../components/common/ResetPasswordModal";
import { formatRepoRole } from "../../../constants/repoRoles";
import { BANK_NBFC_NAME_LABEL } from "../../../constants/companyLabels";
import "../../../styles/users.css";

function CompanyTaxDocumentView({ companyId, kind, uploaded, token }) {
  const [src, setSrc] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!uploaded || !companyId || !token) {
      setSrc("");
      setIsPdf(false);
      setLoadError("");
      return undefined;
    }

    let objectUrl = "";
    let cancelled = false;

    companyService
      .getCompanyTaxDocument(companyId, kind, token)
      .then((blob) => {
        if (cancelled || !blob) return;
        if (blob.type && blob.type.includes("application/json")) {
          throw new Error("Unable to load document");
        }
        objectUrl = URL.createObjectURL(blob);
        setIsPdf(String(blob.type || "").includes("pdf"));
        setSrc(objectUrl);
        setLoadError("");
      })
      .catch(() => {
        if (!cancelled) {
          setSrc("");
          setIsPdf(false);
          setLoadError("Unable to load document");
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [companyId, kind, uploaded, token]);

  if (!uploaded) {
    return <p className="muted tax-doc-upload__empty">No document uploaded</p>;
  }
  if (loadError) {
    return <p className="error-text">{loadError}</p>;
  }
  if (!src) {
    return <p className="muted tax-doc-upload__empty">Loading document…</p>;
  }

  return (
    <div className="tax-doc-upload">
      {!isPdf ? (
        <button
          type="button"
          className="tax-doc-upload__preview--clickable"
          onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
          aria-label="Open full-size document"
          style={{ padding: 0, background: "none" }}
        >
          <img className="tax-doc-upload__preview" src={src} alt="" />
        </button>
      ) : null}
      <button
        type="button"
        className="secondary-page-btn"
        onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
      >
        View full size
      </button>
    </div>
  );
}

export default function SsdiCompanyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();

  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetFlash, setResetFlash] = useState("");
  const [error, setError] = useState("");
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [userStatusFilter, setUserStatusFilter] = useState("all");
  const [usersPagination, setUsersPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [usersLoading, setUsersLoading] = useState(false);
  const [userPendingIds, setUserPendingIds] = useState(() => new Set());
  const [approving, setApproving] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [deletingUserIds, setDeletingUserIds] = useState(() => new Set());

  const loadCompany = useCallback(async () => {
    if (!id || !auth?.token) return;

    try {
      setLoading(true);
      setError("");

      const res = await companyService.getCompanyById(id, auth.token);

      setCompany(res?.data?.company || res?.company || null);
      setSubscription(res?.data?.subscription || res?.subscription || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  }, [id, auth?.token]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  const loadCompanyUsers = useCallback(async () => {
    if (!id || !auth?.token) return;

    try {
      setUsersLoading(true);
      const res = await companyService.getCompanyUsers(id, auth.token, {
        page: usersPage,
        limit: PAGE_LIMIT,
        ...(userStatusFilter !== "all" ? { status: userStatusFilter } : {}),
      });
      const list = res?.data?.users || res?.users || [];
      setCompanyUsers(Array.isArray(list) ? list : []);
      setUsersPagination(readPagination(res));
    } catch {
      setCompanyUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [id, auth?.token, usersPage, userStatusFilter]);

  useEffect(() => {
    setUsersPage(1);
  }, [userStatusFilter]);

  useEffect(() => {
    loadCompanyUsers();
  }, [loadCompanyUsers]);

  const handleMarkPayment = async (method) => {
    if (!company || approving) return;
    const note = window.prompt(
      `Payment note (${method.toLowerCase()}):`,
      company.paymentNote || ""
    );
    if (note === null) return;

    setApproving(true);
    try {
      const res = await companyService.markCompanyPayment(
        company._id || company.id,
        { paymentMethod: method, paymentNote: note },
        auth.token
      );
      const updated = res?.data?.company || res?.company;
      if (updated) setCompany(updated);
      else setCompany((prev) => ({ ...prev, paymentStatus: "PAID", paymentMethod: method }));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to mark payment");
    } finally {
      setApproving(false);
    }
  };

  const handleApprove = async () => {
    if (!company || approving) return;
    if (!window.confirm(`Activate ${company.companyName}? This treats the ₹100 first-user activation as received.`)) return;

    setApproving(true);
    try {
      const res = await companyService.approveCompany(
        company._id || company.id,
        auth.token
      );
      const updated = res?.data?.company || res?.company;
      if (updated) setCompany(updated);
      else setCompany((prev) => ({ ...prev, status: "ACTIVE" }));
      await loadCompanyUsers();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to approve company");
    } finally {
      setApproving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const userId = user._id || user.id;
    if (!userId || deletingUserIds.has(userId)) return;
    const label = user.name || user.email || "this user";
    if (
      !window.confirm(
        `Delete ${label}? This permanently removes the user account. This cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingUserIds((prev) => new Set(prev).add(userId));
    try {
      await companyService.deleteCompanyUser(id, userId, auth.token);
      setCompanyUsers((prev) => prev.filter((u) => (u._id || u.id) !== userId));
      if (String(company?.repoAdminUserId?._id || company?.repoAdminUserId) === String(userId)) {
        await loadCompany();
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete user");
    } finally {
      setDeletingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleDeleteCompany = async () => {
    if (!company || deletingCompany) return;
    const label = company.companyName || company.companyCode || "this company";
    if (
      !window.confirm(
        `Delete ${label}? This permanently removes the company, all users, vehicles, and related data. This cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingCompany(true);
    try {
      await companyService.deleteCompany(company._id || company.id, auth.token);
      navigate("/ssdi/companies");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete company");
      setDeletingCompany(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    const userId = user._id || user.id;
    const nextStatus = !user.isActive;
    const previousStatus = user.isActive;

    setUserPendingIds((prev) => new Set(prev).add(userId));
    setCompanyUsers((prev) =>
      prev.map((u) =>
        (u._id || u.id) === userId ? { ...u, isActive: nextStatus } : u
      )
    );

    try {
      await companyService.updateCompanyUserStatus(
        id,
        userId,
        nextStatus,
        auth.token
      );
    } catch (err) {
      setCompanyUsers((prev) =>
        prev.map((u) =>
          (u._id || u.id) === userId ? { ...u, isActive: previousStatus } : u
        )
      );
      alert(err?.response?.data?.message || "Failed to update user status");
    } finally {
      setUserPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const statusBadgeClass = (status) => {
    const key = (status || "").toLowerCase();
    if (key === "active") return "company-status--active";
    if (key === "pending") return "company-status--pending";
    return "company-status--inactive";
  };

  const handleToggleStatus = () => {
    if (!company || statusUpdating || company.status === "PENDING") return;

    if (company.status === "ACTIVE") {
      setShowBlockModal(true);
      return;
    }

    if (!window.confirm("Unblock this company?")) return;
    applyStatusUpdate("ACTIVE");
  };

  const applyStatusUpdate = async (newStatus, blockReason) => {
    if (!company) return;

    const companyId = company._id || company.id;
    const previous = { ...company };

    setStatusUpdating(true);

    if (newStatus === "INACTIVE") {
      setCompany((prev) => ({
        ...prev,
        status: "INACTIVE",
        blockReason: blockReason || "",
        blockedAt: new Date().toISOString(),
      }));
    } else {
      setCompany((prev) => ({
        ...prev,
        status: "ACTIVE",
        blockReason: null,
        blockedAt: null,
      }));
    }

    try {
      const payload =
        newStatus === "INACTIVE"
          ? { status: "INACTIVE", blockReason }
          : { status: "ACTIVE" };

      const res = await companyService.updateCompany(companyId, payload, auth.token);
      const updated = res?.data?.company || res?.company;
      if (updated) {
        setCompany(updated);
      }
    } catch (err) {
      setCompany(previous);
      alert(err?.response?.data?.message || "Failed to update company status");
    } finally {
      setStatusUpdating(false);
      setShowBlockModal(false);
    }
  };

  const handleBlockConfirm = (reason) => {
    applyStatusUpdate("INACTIVE", reason);
  };

  const handleResetPassword = () => {
    if (!company) return;
    setResetError("");
    setResetFlash("");
    setResetOpen(true);
  };

  const closeResetModal = () => {
    if (resetSubmitting) return;
    setResetOpen(false);
    setResetError("");
  };

  const confirmResetPassword = async (newPassword) => {
    if (!company) return;
    const companyId = company._id || company.id;
    setResetSubmitting(true);
    setResetError("");
    try {
      const res = await companyService.resetRepoAdminPassword(
        companyId,
        newPassword,
        auth.token
      );
      const updatedPassword = res?.data?.repoAdmin?.password || newPassword;
      setResetOpen(false);
      setResetFlash(
        `Repo admin password updated successfully.\nEmail: ${
          company?.repoAdminUserId?.email || "-"
        }\nPassword: ${updatedPassword}`
      );
    } catch (err) {
      setResetError(err?.response?.data?.message || "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  };

  if (loading) {
    return <div className="page">Loading company details...</div>;
  }

  if (error) {
    return (
      <div className="page">
        <p className="error-text">{error}</p>
      </div>
    );
  }

  if (!company) {
    return <div className="page">Company not found.</div>;
  }

  return (
    <div className="page create-company-page">
      <div className="create-company-header">
        <div>
          <h2>Company Details</h2>
          <p className="muted">
            View company profile, repo admin info, and subscription details.
          </p>
        </div>

        <button
          type="button"
          className="secondary-page-btn"
          onClick={() => navigate("/ssdi/companies")}
        >
          Back to Companies
        </button>
      </div>

      {resetFlash && (
        <p className="reset-password-flash" role="status">
          {resetFlash}
        </p>
      )}

      <div className="create-company-form">
        <div className="create-company-card">
          <div className="users-actions" style={{ marginBottom: "20px" }}>
            <h3 style={{ margin: 0 }}>Company Information</h3>

            <span
              className={`company-status ${statusBadgeClass(company.status)}`}
            >
              {company.status}
            </span>
          </div>

          {company.status === "INACTIVE" && company.blockReason && (
            <div className="company-block-reason-banner">
              <strong>Block reason</strong>
              <p>{company.blockReason}</p>
            </div>
          )}

          <div className="form-grid two-column">
            {company.photoUrl ? (
              <div className="form-group full-width">
                <label>Company logo</label>
                <img
                  className="company-detail-photo"
                  src={company.photoUrl}
                  alt=""
                />
              </div>
            ) : null}

            <div className="form-group">
              <label>Company Code</label>
              <input value={company.companyCode || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>{BANK_NBFC_NAME_LABEL}</label>
              <input value={company.companyName || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Registration Date</label>
              <input
                value={
                  company.createdAt
                    ? new Date(company.createdAt).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"
                }
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Activation / Payment</label>
              <input value={company.paymentStatus || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Contact Person / Directors</label>
              <input value={company.contactPersonName || company.ownerName || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Company Email</label>
              <input value={company.email || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>First Confirmation Number</label>
              <input value={company.phone || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Owner Name</label>
              <input value={company.ownerName || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>PAN Number</label>
              <input value={company.panNumber || "-"} readOnly />
              <label>PAN Card</label>
              <CompanyTaxDocumentView
                companyId={id}
                kind="pan"
                uploaded={Boolean(company.panDocumentUploaded)}
                token={auth?.token}
              />
            </div>

            <div className="form-group">
              <label>GST Number</label>
              <input value={company.gstNumber || "-"} readOnly />
              <label>GST Certificate</label>
              <CompanyTaxDocumentView
                companyId={id}
                kind="gst"
                uploaded={Boolean(company.gstDocumentUploaded)}
                token={auth?.token}
              />
            </div>

            <div className="form-group">
              <label>Aadhaar Number</label>
              <input value={company.aadhaarNumber || "-"} readOnly />
              <label>Aadhaar Card</label>
              <CompanyTaxDocumentView
                companyId={id}
                kind="aadhaar"
                uploaded={Boolean(company.aadhaarDocumentUploaded)}
                token={auth?.token}
              />
            </div>

            <div className="form-group full-width">
              <label>Address</label>
              <input value={company.address || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>District / City</label>
              <input
                value={company.district || company?.repoAdminUserId?.district || "-"}
                readOnly
              />
            </div>

            <div className="form-group">
              <label>State</label>
              <input
                value={company.state || company?.repoAdminUserId?.state || "-"}
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Pincode</label>
              <input
                value={company.pincode || company?.repoAdminUserId?.pincode || "-"}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="create-company-card">
          <h3>Repo Admin Information</h3>

          <div className="form-grid two-column">
            {company?.repoAdminUserId?.photoUrl ? (
              <div className="form-group full-width">
                <label>Admin photo</label>
                <img
                  className="company-detail-photo"
                  src={company.repoAdminUserId.photoUrl}
                  alt=""
                />
              </div>
            ) : null}

            <div className="form-group">
              <label>Admin Name</label>
              <input value={company?.repoAdminUserId?.name || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Admin Email</label>
              <input value={company?.repoAdminUserId?.email || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Second Confirmation Number</label>
              <input
                value={company?.repoAdminUserId?.phone || "-"}
                readOnly
              />
              {company?.repoAdminUserId?.phone ? (
                <a
                  href={`tel:${company.repoAdminUserId.phone}`}
                  className="field-hint"
                  style={{ display: "inline-block", marginTop: 6 }}
                >
                  Call admin
                </a>
              ) : null}
            </div>

            <div className="form-group">
              <label>Date of birth</label>
              <input
                value={
                  company.adminDateOfBirth ||
                  company?.repoAdminUserId?.dateOfBirth ||
                  "-"
                }
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Post</label>
              <input
                value={company.adminPost || company?.repoAdminUserId?.post || "-"}
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Admin Role</label>
              <input value={company?.repoAdminUserId?.role || "REPO_ADMIN"} readOnly />
            </div>

            <div className="form-group">
              <label>Admin Account Status</label>
              <input
                value={company?.repoAdminUserId?.isActive ? "Active" : "Inactive"}
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Registration Source</label>
              <input
                value={
                  company?.repoAdminUserId?.registrationSource === "SELF"
                    ? "Self registration"
                    : "Created by SSDI"
                }
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Company Status</label>
              <input value={company.status || "-"} readOnly />
            </div>
          </div>
        </div>

        <div className="create-company-card">
          <h3>Subscription Information</h3>

          <div className="form-grid two-column">
            <div className="form-group">
              <label>Plan Name</label>
              <input value={subscription?.planId?.name || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Subscription Status</label>
              <input value={subscription?.status || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Payment Status</label>
              <input value={subscription?.paymentStatus || "-"} readOnly />
            </div>

            <div className="form-group">
              <label>Start Date</label>
              <input
                value={
                  subscription?.startDate
                    ? new Date(subscription.startDate).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"
                }
                readOnly
              />
            </div>

            <div className="form-group">
              <label>End Date</label>
              <input
                value={
                  subscription?.endDate
                    ? new Date(subscription.endDate).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"
                }
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="create-company-card">
          <h3>Company Users</h3>
          <DataTableToolbar
            onPrint={printTablePage}
            leading={
              <StatusFilterSelect
                value={userStatusFilter}
                onChange={setUserStatusFilter}
                ariaLabel="Filter company users by status"
              />
            }
          />
          {usersLoading ? (
            <p className="muted">Loading users…</p>
          ) : companyUsers.length === 0 ? (
            <p className="muted">No repo users match this status filter.</p>
          ) : (
            <div className="company-table-wrap printable-table-area">
              <table className="users-table excel-grid-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companyUsers.map((user) => {
                    const userId = user._id || user.id;
                    const isUserPending = userPendingIds.has(userId);

                    return (
                      <tr key={userId} className={isUserPending ? "row-pending" : ""}>
                        <td>{user.name || "-"}</td>
                        <td>{user.email || "-"}</td>
                        <td>{user.phone || "-"}</td>
                        <td>{formatRepoRole(user.role)}</td>
                        <td>
                          {user.registrationSource === "SELF" ? "Self" : "Admin"}
                        </td>
                        <td>
                          <span
                            className={`company-status company-status--${
                              user.isActive
                                ? "active"
                                : user.blockedAt
                                  ? "blocked"
                                  : "inactive"
                            }`}
                          >
                            {user.isActive
                              ? "Active"
                              : user.blockedAt
                                ? "Blocked"
                                : "Inactive"}
                          </span>
                        </td>
                        <td className="company-actions-td">
                          <div className="company-actions-cell">
                          <button
                            type="button"
                            className={`company-btn ${
                              user.isActive
                                ? "company-btn--block"
                                : "company-btn--unblock"
                            }`}
                            disabled={isUserPending}
                            onClick={() => handleToggleUserStatus(user)}
                          >
                            {user.isActive ? (
                              <FiShieldOff aria-hidden />
                            ) : (
                              <FiShield aria-hidden />
                            )}
                            <span>
                              {user.isActive ? "Deactivate" : "Activate"}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="company-btn company-btn--delete"
                            disabled={deletingUserIds.has(userId)}
                            onClick={() => handleDeleteUser(user)}
                          >
                            <FiTrash2 aria-hidden />
                            <span>
                              {deletingUserIds.has(userId) ? "Deleting…" : "Delete"}
                            </span>
                          </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <PaginationBar
            page={usersPagination.page || usersPage}
            totalPages={usersPagination.totalPages}
            total={usersPagination.total}
            loading={usersLoading}
            onPageChange={setUsersPage}
          />
        </div>

        <div className="create-company-actions">
          {company.status === "PENDING" ? (
            <>
              {company.paymentStatus !== "PAID" ? (
                <>
                  <button
                    type="button"
                    className="secondary-page-btn"
                    disabled={approving}
                    onClick={() => handleMarkPayment("OFFLINE")}
                  >
                    Mark offline payment
                  </button>
                  <button
                    type="button"
                    className="secondary-page-btn"
                    disabled={approving}
                    onClick={() => handleMarkPayment("ONLINE")}
                  >
                    Mark online payment
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className="primary-page-btn"
                disabled={approving}
                onClick={handleApprove}
              >
                {approving ? "Activating…" : "Activate Company"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="secondary-page-btn"
              disabled={statusUpdating}
              onClick={handleToggleStatus}
            >
              {statusUpdating
                ? "Updating..."
                : company.status === "ACTIVE"
                  ? "Block Company"
                  : "Unblock Company"}
            </button>
          )}

          <button
            type="button"
            className="secondary-page-btn"
            onClick={handleResetPassword}
          >
            Reset Repo Admin Password
          </button>
          <button
            type="button"
            className="company-btn company-btn--delete"
            disabled={deletingCompany}
            onClick={handleDeleteCompany}
          >
            <FiTrash2 aria-hidden />
            <span>{deletingCompany ? "Deleting…" : "Delete Company"}</span>
          </button>
        </div>
      </div>

      <BlockCompanyModal
        open={showBlockModal}
        companyName={company?.companyName}
        loading={statusUpdating}
        onCancel={() => !statusUpdating && setShowBlockModal(false)}
        onConfirm={handleBlockConfirm}
      />
      <ResetPasswordModal
        open={resetOpen}
        subjectName={company?.companyName}
        loading={resetSubmitting}
        error={resetError}
        onCancel={closeResetModal}
        onConfirm={confirmResetPassword}
      />
    </div>
  );
}